import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  StackDivider,
  useDisclosure,
  HStack,
  FormLabel,
  Select
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import useDebounce from '@hooks/useDebounce';
import {
  gomsOnHoldConfig,
  getPendingForHoursOptions
} from '@views/GomsOrderDashboard/utils';
import Pagination from '@components/Pagination';
import GomsOnHoldFulfillmentCard from '@components/GomsOnHoldFulfillmentCard';
import SearchBox from '@components/SearchBox/SearchBox';
import FilterSelect from '@components/FiltersBox/FilterSelect';
import { GOMS_ON_HOLD_FULFILLMENT_DEFAULT_FILTERS } from '@views/GomsOrderDashboard/constant';
import { updateGomsOnHoldFulfillmentsReqData } from '@store/actions/reqDataActions';
import ConfirmModal from '@components/Modal/ConfirmModal';
import { YBL_DASHBOARD_STRING_CONSTANTS } from '@views/RDADashboard/constants';
import ModalLayout from '@components/Modal/ModalLayout';
import { forceInitiateGomsFulfillment } from '@store/actions/gomsFulfillmentActions';
import { setAlert } from '@store/actions/alertActions';
import { ALERT_STATUS_TYPE, SORT_ORDER } from '@constants';
import GomsOrderMetadataModal from '../../../components/Modal/GomsOrderMetadataModal';
import RawDataViewer from '@components/Modal/RawDataViewer.jsx';

const OnHoldFulfillments = ({
  selectedGomsFulfillment,
  setSelectedGomsFulfillment,
  setShowRightSidebar,
  onRecentTransfersClick
}) => {
  const dispatch = useDispatch();

  const {
    data: configData,
    isLoading: isConfigLoading,
    error: configError
  } = useSelector(
    (state) =>
      state.gomsOrderDashboard?.config || {
        data: null,
        isLoading: false,
        error: null
      }
  );

  const searches = configData?.onHoldFulfillments?.searches || [];
  const filters = configData?.onHoldFulfillments?.filters || [];
  const sortKey =
    configData?.onHoldFulfillments?.sorting?.defaultSortKey || 'created_at';
  const sortOrder =
    configData?.onHoldFulfillments?.sorting?.defaultsortOrder ||
    SORT_ORDER.DESC.toLowerCase();

  // Redux States
  const reqData = useSelector(
    (state) => state.reqData.gomsOrderDashboard?.onHoldFulfillments || {}
  );

  const { data, isLoading, error } = useSelector(
    (state) =>
      state.gomsOrderDashboard?.onHoldFulfillments?.fulfillments || {
        data: {},
        isLoading: false,
        error: null
      }
  );

  const { result: onHoldFulfillments, totalPage } = data || {};

  // Request data with proper defaults
  const {
    pageNumber = 0,
    searchKey = '',
    searchValue = '',
    filters: reqFilters = {}
  } = reqData;

  // Local state with proper initialization
  const [filterParams, setFilterParams] = useState(reqFilters || {});
  const [tags, setTags] = useState([]);
  const [searchParams, setSearchParams] = useState({
    searchKey: searchKey || '',
    searchValue: searchValue || ''
  });
  // const [flagFilter, setFlagFilter] = useState('');
  const [sortParams, setSortParams] = useState({
    sortKey,
    sortOrder
  });
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [pendingForHours, setPendingForHours] = useState('96');

  const debouncedFilterParams = useDebounce(filterParams, 500);
  const debouncedSearchParams = useDebounce(searchParams, 500);
  const debouncedSortParams = useDebounce(sortParams, 500);
  const debouncedStuckForHours = useDebounce(pendingForHours, 500);

  const {
    isOpen: isForceInitiateConfirmationModalOpen,
    onOpen: onForceInitiateConfirmationModalOpen,
    onClose: onForceInitiateConfirmationModalClose
  } = useDisclosure();

  const {
    isOpen: flagOpen,
    onOpen: flagOpened,
    onClose: flagClosed
  } = useDisclosure();

  const [activeFlag, setActiveFlag] = useState({
    orderId: null,
    note: '',
    complianceFlagged: false
  });

  const {
    isOpen: isTagsOpen,
    onOpen: onTagsOpen,
    onClose: onTagsClose
  } = useDisclosure();

  const fetchGomsFulfillments = useCallback(() => {
    if (!configData) {
      return;
    }
    dispatch(
      gomsOnHoldConfig.actions.getGomsOnHoldFulfillments({
        payload: {
          pageNumber,
          filters: debouncedFilterParams,
          searchKey: debouncedSearchParams.searchKey,
          searchValue: debouncedSearchParams.searchValue,
          sortKey: debouncedSortParams.sortKey,
          sortOrder: debouncedSortParams.sortOrder,
          pendingForHours: debouncedStuckForHours ? parseInt(debouncedStuckForHours, 10) : undefined
        }
      })
    );
  }, [
    dispatch,
    pageNumber,
    configData,
    debouncedFilterParams,
    debouncedSearchParams,
    debouncedSortParams,
    debouncedStuckForHours
  ]);

  useEffect(() => {
    fetchGomsFulfillments();
  }, [fetchGomsFulfillments]);

  const handleSortChange = useCallback(
    (field, value) => {
      setSortParams((prev) => ({
        ...prev,
        sortOrder: value
      }));

      dispatch(
        updateGomsOnHoldFulfillmentsReqData({
          ...reqData,
          sortOrder: value,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handleStuckForHoursChange = useCallback(
    (e) => {
      const value = e.target.value;
      setPendingForHours(value);

      dispatch(
        updateGomsOnHoldFulfillmentsReqData({
          ...reqData,
          pendingForHours: value ? parseInt(value, 10) : undefined,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handlePageChange = useCallback(
    (page) => {
      const newPageNumber = Math.max(0, page - 1);
      dispatch(
        updateGomsOnHoldFulfillmentsReqData({
          ...reqData,
          pageNumber: newPageNumber
        })
      );
    },
    [dispatch, reqData]
  );

  const handleSearchChange = useCallback(
    (search) => {
      const newSearchParams = {
        searchKey: search.searchKey || '',
        searchValue: search.searchValue || ''
      };

      setSearchParams(newSearchParams);

      dispatch(
        updateGomsOnHoldFulfillmentsReqData({
          ...reqData,
          searchKey: newSearchParams.searchKey,
          searchValue: newSearchParams.searchValue,
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  const handleFilterChange = useCallback(
    ({ key, filters: filterValues }) => {
      setFilterParams((prev) => {
        const updatedParams = { ...prev };
        if (!filterValues || filterValues.length === 0) {
          delete updatedParams[key];
        } else {
          updatedParams[key] = filterValues;
        }
        return updatedParams;
      });

      dispatch(
        updateGomsOnHoldFulfillmentsReqData({
          ...reqData,
          filters: {
            ...reqData.filters,
            [key]: filterValues || []
          },
          pageNumber: 0
        })
      );
    },
    [dispatch, reqData]
  );

  // const handleFlagFilterChange = (e) => {
  //   setFlagFilter?.(e?.target?.value);
  // };

  const handleFlagClick = (orderData) => {
    flagOpened();
    setActiveFlag({
      orderId: orderData?.orderId,
      note: orderData?.additionalDetails?.complianceData?.details,
      complianceFlagged:
        orderData?.additionalDetails?.complianceData?.complianceFlagged
    });
  };

  const handleForceInitiateCleanup = useCallback(
    (e) => {
      setIsModalLoading(false);
      onForceInitiateConfirmationModalClose();
    },
    [onForceInitiateConfirmationModalClose]
  );

  const handleForceInitiate = useCallback((fulfillment) => {
    if (!fulfillment) {
      dispatch(
        setAlert({
          title: 'Unable to perform Force Initiate.',
          status: ALERT_STATUS_TYPE.INFO
        })
      );
      return;
    }
    setIsModalLoading(true);
    dispatch(
      forceInitiateGomsFulfillment({
        fulfillmentId: fulfillment?.fulfillmentId,
        onComplete: handleForceInitiateCleanup
      })
    );
  }, []);

  const renderLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          Loading fulfillments...
        </Text>
      </VStack>
    </Flex>
  );

  // Render config loading state
  const renderConfigLoadingState = () => (
    <Flex justify="center" align="center" w="full" h="200px">
      <VStack spacing={3}>
        <Spinner thickness="4px" size="lg" color="colorPrimary" />
        <Text fontSize="sm" color="whiteAlpha.600">
          Loading configuration...
        </Text>
      </VStack>
    </Flex>
  );

  // Render error state
  const renderErrorState = () => (
    <Box
      p={4}
      bg="backGround"
      border="1px solid"
      borderColor="red.300"
      borderRadius="xl"
    >
      <Text color="red.500" fontSize="sm">
        Error loading fulfillments: {error}
      </Text>
    </Box>
  );

  // Render empty state
  const renderEmptyState = () => (
    <Box
      p={8}
      bg="backGround"
      border="1px solid"
      borderColor="whiteAlpha.300"
      borderRadius="xl"
      textAlign="center"
    >
      <Text color="whiteAlpha.700" fontSize="lg">
        No On Hold Fulfillments Found
      </Text>
      <Text color="whiteAlpha.500" fontSize="sm" mt={2}>
        There are currently no fulfillments on hold.
      </Text>
    </Box>
  );

  // Render fulfillments list
  const renderFulfillmentsList = () => (
    <VStack spacing={4} align="stretch">
      {onHoldFulfillments.map((fulfillment) => (
        <GomsOnHoldFulfillmentCard
          key={fulfillment?.fulfillmentId}
          fulfillmentData={fulfillment}
          selectedFulfillment={selectedGomsFulfillment}
          setSelectedFulfillment={setSelectedGomsFulfillment}
          onRecentTransfersClick={onRecentTransfersClick}
          handleFlagClick={() => handleFlagClick(fulfillment)}
          handleTagClick={() => {
            setTags(fulfillment?.tags);
            onTagsOpen();
          }}
        />
      ))}
    </VStack>
  );

  // Main render function
  const renderContent = () => {
    if (isLoading) return renderLoadingState();
    if (error) return renderErrorState();
    if (!onHoldFulfillments?.length) return renderEmptyState();
    return renderFulfillmentsList();
  };

  const renderModals = () => {
    return (
      <>
        <ModalLayout
          isOpen={isForceInitiateConfirmationModalOpen}
          onClose={onForceInitiateConfirmationModalClose}
        >
          <ConfirmModal
            prompt={
              YBL_DASHBOARD_STRING_CONSTANTS.RE_INITIALIZE_STUCK_TRANSFER_CONFIRMATION_MODAL_PROMPT
            }
            handleSubmit={handleForceInitiate}
            isLoading={isModalLoading}
          />
        </ModalLayout>
        <ModalLayout isOpen={flagOpen} onClose={flagClosed}>
          <GomsOrderMetadataModal
            orderId={activeFlag?.orderId}
            onClose={() => {
              fetchGomsFulfillments();
              flagClosed();
            }}
            initNote={activeFlag?.note}
            initFlag={activeFlag?.complianceFlagged}
            title={'Update compliance flag'}
            successMessage={`Updated compliance flag for order ${activeFlag?.orderId}`}
            failureMessage={`Failed to update compliance flag for order ${activeFlag?.orderId}`}
          />
        </ModalLayout>
        <ModalLayout isOpen={isTagsOpen} onClose={onTagsClose}>
          <RawDataViewer title={'Tags'} data={tags} />
        </ModalLayout>
      </>
    );
  };

  if (isConfigLoading) {
    return (
      <Box h="100%" overflow="hidden">
        <VStack h="100%" justify="center" align="center">
          {renderConfigLoadingState()}
        </VStack>
      </Box>
    );
  }

  const hasValidConfig =
    configData &&
    (searches?.length > 0 ||
      filters?.length > 0 ||
      typeof filters === 'object');

  return (
    <Box h="100%" overflow="hidden">
      {renderModals()}
      <VStack
        h="100%"
        overflowY="auto"
        overflowX="hidden"
        alignItems="stretch"
        gap={4}
        px={4}
        pb={4}
        divider={<StackDivider borderColor="whiteAlpha.300" />}
      >
        {/* Header */}
        <Flex justify="space-between" align="center">
          <Heading size="md">On Hold Fulfillments</Heading>
          {!isLoading && onHoldFulfillments?.length > 0 && (
            <Text fontSize="sm" color="whiteAlpha.600">
              {onHoldFulfillments.length} fulfillment
              {onHoldFulfillments.length !== 1 ? 's' : ''}
            </Text>
          )}
        </Flex>

        {/* Search & Filters - Only show when config is available */}
        {hasValidConfig && (
          <VStack
            justifyContent="flex-start"
            alignItems="start"
            alignSelf="flex-start"
            gap={2}
          >
            {/* Search Box */}
            {Array.isArray(searches) && searches.length > 0 && (
              <SearchBox
                searches={searches}
                handleSearchChange={handleSearchChange}
                isDisabled={isLoading}
              />
            )}

            {/* Filter Select */}
            {((Array.isArray(filters) && filters.length > 0) ||
              (typeof filters === 'object' &&
                filters !== null &&
                Object.keys(filters).length > 0)) && (
                <FilterSelect
                  handleFilterSubmit={handleFilterChange}
                  filtersData={filters}
                  defaultFiltersData={GOMS_ON_HOLD_FULFILLMENT_DEFAULT_FILTERS}
                />
              )}

            <HStack spacing={4} alignItems="end">
              {/*<VStack mt={2}>*/}
              {/*  <FormLabel fontSize="sm" mb={0}>Compliance Flag</FormLabel>*/}
              {/*  <Select*/}
              {/*    bg="altGray.200"*/}
              {/*    color="shadedGray.50"*/}
              {/*    focusBorderColor="brand.400"*/}
              {/*    style={{ border: 0, borderRadius: '12px' }}*/}
              {/*    value={flagFilter}*/}
              {/*    onChange={handleFlagFilterChange}*/}
              {/*  >*/}
              {/*    <option value="">show all</option>*/}
              {/*    <option value="true">Flagged</option>*/}
              {/*    <option value="false">Not Flagged</option>*/}
              {/*  </Select>*/}
              {/*</VStack>*/}
              {/* Sort Order Selection */}
              <VStack alignItems="start" spacing={1}>
                <FormLabel fontSize="sm" mb={0}>
                  Sort
                </FormLabel>
                <Select
                  bg="#414141"
                  color="shadedGray.50"
                  focusBorderColor="brand.400"
                  border="none"
                  borderRadius="12px"
                  gap={3}
                  m={0}
                  w="auto"
                  _hover={{ bg: 'altGray.300' }}
                  value={sortParams.sortOrder}
                  onChange={(e) =>
                    handleSortChange('sortOrder', e.target.value)
                  }
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </Select>
              </VStack>

              {/* Order Stuck for hours */}
              <VStack alignItems="start" spacing={1}>
                <FormLabel fontSize="sm" mb={0}>
                  Pending For
                </FormLabel>
                <Select
                  bg="#414141"
                  color="shadedGray.50"
                  focusBorderColor="brand.400"
                  placeholder="Pending for all"
                  border="none"
                  borderRadius="12px"
                  gap={3}
                  m={0}
                  w="auto"
                  _hover={{ bg: 'altGray.300' }}
                  value={pendingForHours}
                  onChange={handleStuckForHoursChange}
                >
                  {getPendingForHoursOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </VStack>
            </HStack>
          </VStack>
        )}

        {/* Pagination */}
        {!isLoading && !error && totalPage > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            currentPage={pageNumber + 1}
            totalPages={totalPage}
          />
        )}

        {/* Content */}
        {renderContent()}
      </VStack>
    </Box>
  );
};

export default OnHoldFulfillments;
